import {
  DataProps,
  DeleteOcrResponse,
  GetFinalizedPaymentProofsResponse,
  GetOcrResponse,
  SubmitPaymentProofsRequest,
  SubmitPaymentProofsResponse,
  UploadOcrResponse,
} from '@diamond/shared/types';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError, isAxiosError } from 'axios';

export const OCR_PAYMENT_PROOF_QUERY_KEY = ['payment-proof-ocr'] as const;
export const FINALIZED_PAYMENT_PROOF_QUERY_KEY = [
  'finalized-payment-proof',
] as const;

const ocrUrl = '/order/payment-proof/ocr';
const orderPaymentsUrl = '/order-payments';
const paymentProofUrl = '/order/payment-proof';

export const uploadOcrPaymentProof = async (
  token: string,
  orderPaymentId: string,
  files: File[]
): Promise<UploadOcrResponse> => {
  const formData = new FormData();
  formData.append('order_payment_id', orderPaymentId);
  files.forEach((file, index) =>
    formData.append(`file-${index}`, file, file.name)
  );

  try {
    const { data } = await apiInstance(token).post<UploadOcrResponse>(
      ocrUrl,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(axiosError.response?.data as DataProps, (scope) => {
        scope.setTag(
          'UploadOcrPaymentProof',
          `orderPaymentId ${orderPaymentId}`
        );
        scope.setLevel('error');
      });
    }
    throw error;
  }
};

export const getOcrPaymentProofItems = async (
  token: string,
  orderPaymentId: string
): Promise<GetOcrResponse> => {
  try {
    const { data } = await apiInstance(token).get<GetOcrResponse>(ocrUrl, {
      params: { order_payment_id: orderPaymentId },
    });

    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(axiosError.response?.data as DataProps, (scope) => {
        scope.setTag(
          'GetOcrPaymentProofItems',
          `orderPaymentId ${orderPaymentId}`
        );
        scope.setLevel('error');
      });
    }
    throw error;
  }
};

export const getFinalizedPaymentProofs = async (
  token: string,
  orderPaymentId: string
): Promise<GetFinalizedPaymentProofsResponse> => {
  try {
    const { data } = await apiInstance(token).get<GetFinalizedPaymentProofsResponse>(
      `${paymentProofUrl}/${orderPaymentId}/finalized`
    );

    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(axiosError.response?.data as DataProps, (scope) => {
        scope.setTag(
          'GetFinalizedPaymentProofs',
          `orderPaymentId ${orderPaymentId}`
        );
        scope.setLevel('error');
      });
    }
    throw error;
  }
};

export const deleteOcrPaymentProof = async (
  token: string,
  id: string
): Promise<DeleteOcrResponse> => {
  const { data } = await apiInstance(token).delete<DeleteOcrResponse>(
    `${ocrUrl}/${id}`
  );
  return data;
};

export const submitOcrPaymentProofs = async (
  token: string,
  paymentId: string,
  body: SubmitPaymentProofsRequest
): Promise<SubmitPaymentProofsResponse> => {
  try {
    const { data } = await apiInstance(token).post<SubmitPaymentProofsResponse>(
      `${orderPaymentsUrl}/${paymentId}/payment-proofs`,
      body
    );
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;
      buyerCaptureException(axiosError.response?.data as DataProps, (scope) => {
        scope.setTag('SubmitOcrPaymentProofs', `paymentId ${paymentId}`);
        scope.setLevel('error');
      });
    }
    throw error;
  }
};
